"use client";

import React, { useState, useEffect, useRef } from 'react';
import styles from './HeaderBurger.module.scss';
import {
  FaBars,
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaXTwitter,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa6';
import { FaTimes } from 'react-icons/fa';
import { Button, Loader } from '@mantine/core';
import Link from 'next/link';
import { useAuthValidateWithoutRedirect, useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import Cookies from 'js-cookie';
import { usePathname, useRouter } from 'next/navigation';
import HeaderEditorModal from '../HeaderMenu/HeaderEditorModal';
import { parseJwt } from '@/src/helpers/decode-jwt';

interface SubLink {
  title?: string;
  label: string;
  link: string;
}

interface NavLink {
  title?: string;
  label: string;
  link?: string;
  links?: SubLink[];
}

interface HeaderData {
  header: NavLink[];
  metaHeader?: NavLink[];
  backgroundUrl?: string;
  languages: string[];
  dashboard: string;
  login: string;
  logout: string;
}

interface HeaderBurgerProps {
  data: HeaderData;
  theme?: 'light' | 'dark';
  color?: string;
  locale?: 'fr' | 'en';
}

const languageFlags: Record<string, string> = {
  fr: '🇫🇷',
  en: '🇬🇧',
};

const HeaderBurger: React.FC<HeaderBurgerProps> = ({
  data,
  theme = 'dark',
  color = '#ffcc00'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<SubLink[] | null>(null);
  const [submenuTitle, setSubmenuTitle] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      try {
        const decoded = token ? parseJwt(token) : null;
        if (decoded.role === 'superAdmin' || decoded.role === 'admin') {
          setIsSuperAdmin(true);
        }
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
    setSubmenuTitle('');
  };

  const logout = () => {
    closeMenu();
    Cookies.remove('authToken');
    location.reload();
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const themeClass = theme === 'light' ? styles.lightTheme : styles.darkTheme;

  const { isLoggedIn } = useAuthValidateWithoutRedirect();
  const pathname = usePathname();

  return (
    <div className={styles.headerBurgerContainer} style={{ '--highlight-color': color } as React.CSSProperties}>
      <button className={styles.burgerIcon} onClick={toggleMenu} aria-label="Toggle menu">
        <FaBars />
      </button>

      <div
        className={`${styles.burgerMenuOverlay} ${isOpen ? styles.open : ''}`}
        onClick={closeMenu}
      />

      <div
        className={`${styles.burgerMenu} ${themeClass} ${isOpen ? styles.open : ''}`}
        ref={menuRef}
        aria-hidden={!isOpen}
        style={{ '--highlight-color': color } as React.CSSProperties}
      >
        <button className={styles.closeBtn} onClick={closeMenu} aria-label="Close menu">
          <FaTimes />
        </button>

        <nav className={styles.burgerNav} style={{ backgroundImage: `url(${data.backgroundUrl})` }}>
          <div className={styles.burgerContent}>
            <div className={styles.connexionWrapper}>
              {isSuperAdmin && <HeaderEditorModal mainHeader={JSON.parse(JSON.stringify(data.header))} />}
              {isLoggedIn ?
                <>
                  <Button
                    className={styles.connexionBtn}
                    variant="outline"
                    size="sm"
                    component={Link}
                    href="/admin"
                    onClick={closeMenu}
                  >
                    {data.dashboard}
                  </Button>
                  <Button
                    className={styles.logoutBtn}
                    variant="outline"
                    size="sm"
                    onClick={logout}
                  >
                    {data.logout}
                  </Button>
                </> : <Button
                  className={styles.connexionBtn}
                  variant="outline"
                  size="sm"
                  component={Link}
                  href="/admin"
                  onClick={closeMenu}
                >
                  {data.login}
                </Button>}
            </div>
            {!activeSubmenu ? (
              <ul className={styles.burgerLinks}>
                {data.header.map((link, idx) => (
                  <React.Fragment key={idx}>
                    {link.title && (
                      <li className={styles.sectionTitle}>
                        <span>{link.title}</span>
                      </li>
                    )}
                    <li className={styles.link}>
                      {link.link && !link.links ? (
                        <Link href={link.link} onClick={closeMenu}>
                          {link.label}
                        </Link>
                      ) : link.links ? (
                        <Button
                          className={`${styles.submenuBtn}`}
                          onClick={() => {
                            setActiveSubmenu(link.links || []);
                            setSubmenuTitle(link.label);
                          }}
                        >
                          <span className={styles.submenuBtnText}>{link.label}</span>
                          <FaChevronRight />
                        </Button>
                      ) : null}
                    </li>
                  </React.Fragment>
                ))}
              </ul>
            ) : (
              <>
                <button className={styles.backBtn} onClick={() => setActiveSubmenu(null)}>
                  <FaChevronLeft /> <span className={styles.backText}>{submenuTitle}</span>
                </button>
                <ul className={`${styles.burgerLinks} ${styles.submenuView}`}>
                  {activeSubmenu.map((sublink, idx) => (
                    <React.Fragment key={idx}>
                      {sublink.title && (
                        <li className={styles.sectionTitle}>
                          <span>{sublink.title}</span>
                        </li>
                      )}
                      <li className={styles.link}>
                        <Link href={sublink.link} onClick={closeMenu}>
                          {sublink.label}
                        </Link>
                      </li>
                    </React.Fragment>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className={styles.bottomFixedActions}>
            {/* <div className={styles.burgerLanguages}>
              {data.languages.map((lang) => (
                <Button
                  key={lang}
                  onClick={() => {
                    closeMenu();
                    router.push(`/${lang}${pathname.replace(/^\/(en|fr)/, '')}`);
                  }}
                  aria-label={`Switch to ${lang}`}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div> */}
            <div className={styles.socialIcons}>
              {/* <a href="#" aria-label="Instagram"><FaInstagram /></a> */}
              <a href="https://www.facebook.com/lisaseror" aria-label="Facebook"><FaFacebookF /></a>
              {/* <a href="#" aria-label="LinkedIn"><FaLinkedinIn /></a>
              <a href="#" aria-label="X"><FaXTwitter /></a> */}
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default HeaderBurger;
